/**
 * Package builder (PRD #1761): the 2.1 "paketbyggaren" ported as one Alpine
 * component. It owns every editing state; the server is only asked to load,
 * search and save through the quotes.package-builder Livewire component.
 *
 * Registered on alpine:init so it survives deferred mounting and runs under
 * Vitest with a stubbed $wire.
 */
const CatalogCap = 80;
const CatalogWidthKey = 'sb_paket_catalog_w';
const CatalogMinWidth = 220;
const CatalogMaxWidth = 560;
const CatalogDefaultWidth = 320;
const VatViewKey = 'sb_paket_vat_view';
const SearchDelayMs = 300;
const MultiWidth = 260;
const ServerSorts = ['rel', 'priceup', 'pricedown', 'margin'];

const PickerWidth = 264;
const PickerHeight = 340;
const MonthsShort = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const Months = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];

const money = (value) => String(Math.round(Number(value) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
/** The note as it is saved: trimmed, and null when nothing but whitespace was written. */
const noteText = (value) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);
const storedAmount = (typed, ratio, places) => {
    if (typed === '' || !Number.isFinite(Number(typed))) return typed;
    if (ratio === 1) return Number(typed);
    return Math.round(Number(typed) * ratio * 10 ** places) / 10 ** places;
};
const pad = (value) => (value < 10 ? '0' : '') + value;
/** Local calendar date as YYYY-MM-DD (no UTC drift from toISOString). */
const isoOf = (date) => date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
const parseIso = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};
/** The date picker under its field, or above it when there is no room below, kept inside the viewport. */
const pickerStyle = (anchor, height) => {
    const rect = anchor?.getBoundingClientRect?.() ?? { left: 0, top: 0, bottom: 0 };
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PickerWidth - 8));
    const top = rect.bottom + height + 6 <= window.innerHeight - 8
        ? rect.bottom + 6 : Math.max(8, Math.min(rect.top - height - 6, window.innerHeight - height - 8));
    return 'display:block;left:' + left + 'px;top:' + top + 'px';
};
const isoDays = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return isoOf(date);
};

/** The catalogue state at opening: no query, relevance, every chip at its "all" position, both kinds shown, the filters folded away. */
const freshCatalog = () => ({ query: '', sort: 'rel', stock: [], margin: 'all', campaign: 'all', showPackages: true, showProducts: true, filtersOpen: false, items: [], total: 0, loading: false, searched: false });

const readWidth = () => {
    try {
        const stored = parseInt(localStorage.getItem(CatalogWidthKey), 10);
        if (Number.isFinite(stored)) return Math.min(CatalogMaxWidth, Math.max(CatalogMinWidth, stored));
    } catch (error) {
        // Storage can be unavailable (private mode, blocked site data); the default width is fine.
    }
    return CatalogDefaultWidth;
};

const storeWidth = (width) => {
    try {
        localStorage.setItem(CatalogWidthKey, String(width));
    } catch (error) {
        // Nothing to remember without storage.
    }
};

const readExVat = () => {
    try {
        return localStorage.getItem(VatViewKey) === 'ex';
    } catch (error) {
        return false;
    }
};

const storeExVat = (exVat) => {
    try {
        localStorage.setItem(VatViewKey, exVat ? 'ex' : 'inc');
        return true;
    } catch (error) {
        return false;
    }
};

/** Vue can place template children outside .content; move them back and let Alpine clone the rows. */
const repairTemplates = (scope) => {
    for (const template of scope.querySelectorAll('template')) {
        if (!template.content.childNodes.length && template.childNodes.length) {
            template.content.append(...template.childNodes);
        }
        template.content.firstElementChild?.removeAttribute('x-ignore');
        repairTemplates(template.content);
    }
};

document.addEventListener('alpine:init', () => {
    Alpine.data('packageBuilder', () => ({
        open: false,
        loading: false,
        serverToday: new Date().toISOString().slice(0, 10),
        loadSucceeded: false,
        saving: false,
        error: '',
        customer: 'kunden',
        packages: [],
        draft: null,
        get draftPayload() {
            return JSON.stringify(this.draft ? { ...this.draft.payload, displayPackages: this.draft.packages } : { packages: [], settings: {} });
        },
        profile: { discountType: 'kr', allowsLineDiscount: true, priceLockedForDays: null, paymentMethods: [], paymentRow: false, vatPercentage: 0 },
        features: { vera: false, configurable: false },
        configPanel: null,
        quote: { isTemplate: false, ignoreOutOfStock: false, ignoreDeleted: false, amountsEditable: false, signable: true, signableUntil: null },
        /** The quote-wide permissions shown in the Advanced menu. */
        advancedSettings: [
            { key: 'ignoreOutOfStock', label: 'Tillåt köp av produkter som är slut i lager' },
            { key: 'ignoreDeleted', label: 'Tillåt köp av utgångna produkter' },
            { key: 'amountsEditable', label: 'Kunden kan ändra antal produkter' },
            { key: 'signable', label: 'Tillåt signering och köp' },
        ],
        settings: { ignoreOutOfStock: false, ignoreDeleted: false, amountsEditable: false, signable: true },
        adv: false,
        /** Which channels the quote-updated message can reach the customer on, and which the seller ticked. */
        available: { sms: false, email: false, chat: false },
        notify: { sms: false, email: false, chat: false },
        baseline: '',
        /** The server's hash of the customer-editable state at load; save refuses when the customer changed it since. */
        fingerprint: null,
        catalog: freshCatalog(),
        selected: null,
        multi: { open: false, item: null, checked: [], style: 'display:none' },
        discardOpen: false,
        warnOpen: false,
        web: { open: false, url: '' },
        picker: { open: false, pi: null, field: null, year: 0, month: 0, style: 'display:none' },
        pickerAnchor: null,
        catalogWidth: CatalogDefaultWidth,
        exVat: false,
        resizing: false,
        dragging: null,
        rowDrag: null,
        dropMark: null,
        seq: 0,
        actionSeq: 0,
        revision: 0,
        timer: null,
        resizeMove: null,
        resizeStop: null,

        init() {
            Alpine.mutateDom(() => repairTemplates(this.$root));
            this.catalogWidth = readWidth();
            this.exVat = readExVat();
            try {
                const restored = JSON.parse(this.$root.dataset.initialDraft || 'null');
                if (Array.isArray(restored?.displayPackages) && Array.isArray(restored?.packages)) {
                    this.draft = { packages: restored.displayPackages, payload: { packages: restored.packages, settings: restored.settings ?? {} } };
                }
            } catch { /* An invalid previous submission is corrected in the builder. */ }
            this.submitGuard = (event) => {
                if (this.open) {
                    event.preventDefault();
                    return;
                }
                if (this.draft) {
                    this.readFormSwitches(this.draft.payload.settings);
                    event.currentTarget.elements.namedItem('builder_payload').value = this.draftPayload;
                }
            };
            this.$root.closest('form')?.addEventListener('submit', this.submitGuard);
        },
        destroy() {
            this.$root.closest('form')?.removeEventListener('submit', this.submitGuard);
            clearTimeout(this.timer);
            this.stopResize();
        },

        // Opening, closing and the dirty check
        async openBuilder() {
            if (this.open) return;
            this.open = true;
            this.error = '';
            this.discardOpen = false;
            this.adv = false;
            this.selected = null;
            this.closeMulti();
            this.closePicker();
            this.catalog = freshCatalog();
            await this.load();
            this.search();
        },
        async load() {
            this.loading = true;
            this.loadSucceeded = false;
            try {
                const result = await this.$wire.load();
                this.serverToday = result.today ?? new Date().toISOString().slice(0, 10);
                this.features = { vera: false, configurable: false, ...(result.features ?? {}) };
                this.customer = result.customer || 'kunden';
                this.profile = { ...this.profile, ...(result.profile ?? {}) };
                if (this.profile.createMode) this.customer = this.$root.closest('form')?.elements.namedItem('customer_name')?.value?.trim() || this.customer;
                this.quote = { ...this.quote, ...(result.quote ?? {}) };
                if (this.profile.createMode) {
                    const form = this.$root.closest('form');
                    const signing = form?.elements.namedItem('signable');
                    const deadline = form?.elements.namedItem('signable_until');
                    if (signing) this.quote.signable = signing.checked;
                    if (deadline) this.quote.signableUntil = deadline.value || null;
                }
                this.restoreSettings();
                this.available = { sms: false, email: false, chat: false, ...(result.notify ?? {}) };
                this.notify = { sms: false, email: false, chat: false };
                this.fingerprint = result.fingerprint ?? null;
                this.fresh(this.profile.createMode && this.draft ? this.draft.packages : (result.packages ?? []));
                if (this.profile.createMode && this.draft) this.settings = { ...this.settings, ...this.draft.payload.settings };
                if (this.profile.createMode) this.readFormSwitches();
                this.snapshot();
                this.loadSucceeded = true;
            } catch (error) {
                this.error = 'Erbjudandet kunde inte läsas in. Försök igen.';
                this.report(error);
            } finally {
                this.loading = false;
            }
        },
        requestClose() {
            if (!this.open || this.saving) return;
            if (this.multi.open) this.closeMulti();
            if (this.isDirty) {
                this.discardOpen = true;
                this.$nextTick(() => this.$refs.keepEditing?.focus());
                return;
            }
            this.closeBuilder();
        },
        keepEditing() {
            this.discardOpen = false;
        },
        discard() {
            this.discardOpen = false;
            this.closeBuilder();
        },
        closeBuilder() {
            this.open = false;
            this.adv = false;
            this.closeMulti();
            this.warnOpen = false;
            this.closeWeb();
            this.closeConfig();
            this.closePicker();
            this.selected = null;
            this.dragging = null;
            this.dragRowEnd();
        },
        /** Escape closes the innermost thing first: date picker, Advanced menu, popover, low-margin warning, web preview, discard dialog, then the builder. */
        onEscape() {
            if (!this.open) return;
            if (this.configPanel) return this.closeConfig();
            if (this.picker.open) return this.closePicker();
            if (this.adv) return (this.adv = false);
            if (this.multi.open) return this.closeMulti();
            if (this.warnOpen) return this.closeWarn();
            if (this.web.open) return this.closeWeb();
            if (this.discardOpen) return this.keepEditing();
            this.requestClose();
        },
        /** The packages and the Advanced settings are what a save changes; the notify choice is not. */
        state() {
            return JSON.stringify({ packages: this.payload(), settings: this.settingsPayload() });
        },
        get isDirty() {
            return this.state() !== this.baseline;
        },
        snapshot() {
            this.baseline = this.state();
        },
        settingsPayload() {
            return { ignoreOutOfStock: Boolean(this.settings.ignoreOutOfStock), ignoreDeleted: Boolean(this.settings.ignoreDeleted), amountsEditable: Boolean(this.settings.amountsEditable), signable: Boolean(this.settings.signable) };
        },
        restoreSettings() {
            this.settings = { ignoreOutOfStock: Boolean(this.quote.ignoreOutOfStock), ignoreDeleted: Boolean(this.quote.ignoreDeleted), amountsEditable: Boolean(this.quote.amountsEditable), signable: Boolean(this.quote.signable) };
        },
        readFormSwitches(settings = this.settings) {
            const form = this.$root.closest('form');
            for (const [key, name] of [['signable', 'signable'], ['amountsEditable', 'amounts_editable']]) {
                const input = form?.elements.namedItem(name);
                if (input) settings[key] = input.checked;
            }
        },
        applyFormSwitches() {
            const form = this.$root.closest('form');
            for (const [key, name] of [['signable', 'signable'], ['amountsEditable', 'amounts_editable']]) {
                const input = form?.elements.namedItem(name);
                if (input && input.checked !== this.settings[key]) {
                    input.checked = this.settings[key];
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        },
        notifyPayload() {
            return { sms: Boolean(this.notify.sms), email: Boolean(this.notify.email), chat: false };
        },
        toggleNotify(channel) {
            if (!this.available[channel]) return;
            this.notify[channel] = !this.notify[channel];
        },

        // Packages
        emptyPkg() {
            return {
                key: 'p' + ++this.seq,
                id: null,
                name: '',
                recommended: false,
                disc: { mode: this.profile.discountType === 'pct' ? 'pct' : 'kr', val: 0 },
                discount_valid_until: null,
                signable_until: this.quote.signableUntil ?? null,
                price_locked_until: this.profile.priceLockedForDays !== null && this.profile.priceLockedForDays !== undefined
                    ? isoDays(num(this.profile.priceLockedForDays))
                    : null,
                pay: null,
                discOpen: false,
                veraPrompt: '',
                veraThinking: false,
                items: [],
            };
        },
        fresh(packages) {
            this.packages = packages.map((pkg) => ({
                ...this.emptyPkg(),
                ...pkg,
                disc: { mode: pkg.disc?.mode === 'pct' ? 'pct' : 'kr', val: num(pkg.disc?.val) },
                items: (pkg.items ?? []).map((item) => this.lineFrom(item, item.qty)),
            }));
            this.normalize();
        },
        /** Keeps the non-empty packages, exactly one empty package at the end, and at most one star, on a non-empty package. */
        normalize() {
            const filled = this.packages.filter((pkg) => pkg.items.length > 0);
            const lastEmpty = this.packages.length && this.packages[this.packages.length - 1].items.length === 0
                ? this.packages[this.packages.length - 1]
                : this.emptyPkg();
            lastEmpty.recommended = false;
            const starred = filled.findIndex((pkg) => pkg.recommended);
            filled.forEach((pkg, index) => { pkg.recommended = index === starred; });
            this.packages = [...filled, lastEmpty];
        },
        packageLabel(pi) {
            return this.packages[pi]?.name || 'Alternativ ' + (pi + 1);
        },
        get realCount() {
            return this.packages.filter((pkg) => pkg.items.length > 0).length;
        },

        // Column header controls: star, arrows, copy, remove
        /** Stars the package and unstars any other; starring the starred package again removes the recommendation. */
        recommend(pi) {
            if (!this.packages[pi] || this.packages[pi].items.length === 0) return;
            const on = !this.packages[pi].recommended;
            this.packages.forEach((pkg, index) => { pkg.recommended = on && index === pi; });
        },
        canMove(pi, dir) {
            const neighbour = this.packages[pi + dir];
            return Boolean(this.packages[pi]?.items.length && neighbour && neighbour.items.length > 0);
        },
        movePkg(pi, dir) {
            if (!this.canMove(pi, dir)) return;
            const next = [...this.packages];
            [next[pi], next[pi + dir]] = [next[pi + dir], next[pi]];
            this.packages = next;
        },
        /** A deep copy of the package, never starred, placed in the next empty slot after it. */
        copyPkg(pi) {
            const src = this.packages[pi];
            if (!src || src.items.length === 0) return;
            const copy = {
                ...this.emptyPkg(),
                name: src.name ? src.name + ' (kopia)' : '',
                disc: { mode: src.disc.mode, val: num(src.disc.val) },
                discount_valid_until: src.discount_valid_until ?? null,
                signable_until: src.signable_until ?? null,
                price_locked_until: src.price_locked_until ?? null,
                pay: Array.isArray(src.pay) ? [...src.pay] : null,
                items: src.items.map((line) => this.copiedLine(line)),
            };
            const keys = new Map(src.items.map((line, index) => [line.key, copy.items[index].key]));
            for (const line of copy.items) if (keys.has(line.parent)) line.parent = keys.get(line.parent);
            const slot = this.packages.findIndex((pkg, index) => index > pi && pkg.items.length === 0);
            if (slot === -1) this.packages.push(copy); else this.packages.splice(slot, 1, copy);
            this.normalize();
        },
        removePkg(pi) {
            if (!this.packages[pi]) return;
            this.packages.splice(pi, 1);
            this.normalize();
        },

        // Payment and signing methods per package
        /** A package without an override (null) offers every method the profile has on. */
        payOn(pkg, id) {
            return pkg.pay == null || pkg.pay.includes(id);
        },
        /**
         * Switches one method on or off for a package. The last method on stays on, and a
         * package with every method on is stored as the profile default (null) so the row
         * never narrows what the customer sees by accident.
         */
        togglePay(pkg, id) {
            const all = this.profile.paymentMethods.map((method) => method.id);
            const current = all.filter((method) => this.payOn(pkg, method));
            const next = current.includes(id)
                ? current.filter((method) => method !== id)
                : all.filter((method) => method === id || current.includes(method));
            if (next.length === 0) return;
            pkg.pay = next.length === all.length ? null : next;
        },

        // Hidden configuration UI only; no product currently supplies option groups.
        openConfig(line) {
            if (!this.features.configurable || !line.optionGroups.length) return;
            const defaults = Object.fromEntries(line.optionGroups.map((group) => [group.id,
                group.default ?? (group.type === 'number' ? (group.min ?? 0) : (group.values?.[0]?.id ?? '')),
            ]));
            this.configPanel = { key: line.key, product: JSON.parse(JSON.stringify(line)), values: { ...defaults, ...line.config } };
        },
        closeConfig() {
            this.configPanel = null;
        },
        configOptions(group) {
            if (group.type !== 'number') return (group.values ?? []).map((value) => ({
                id: value.id,
                label: value.label + (value.priceDelta ? ' (+' + this.money(value.priceDelta) + ' kr)' : ''),
            }));
            const min = num(group.min ?? group.default);
            const max = num(group.max ?? min);
            const step = Math.max(1, num(group.step ?? 1));
            return Array.from({ length: Math.max(0, Math.min(1000, Math.floor((max - min) / step) + 1)) }, (_, index) => {
                const value = min + index * step;
                return { id: value, label: value + (group.unit ? ' ' + group.unit : '') };
            });
        },
        setConfigValue(group, value) {
            if (!this.configPanel) return;
            this.configPanel.values[group.id] = group.type === 'number' ? num(value) : value;
        },
        get configName() {
            if (!this.configPanel) return '';
            const { product, values } = this.configPanel;
            const axes = product.nameAxes ?? product.optionGroups.map((group) => group.id);
            const parts = [];
            if (axes.includes('bredd') && axes.includes('hojd')) parts.push(values.bredd + '×' + values.hojd + ' cm');
            for (const group of product.optionGroups) {
                if (!axes.includes(group.id) || ['bredd', 'hojd'].includes(group.id)) continue;
                const label = group.type === 'number' ? values[group.id] + (group.unit ? ' ' + group.unit : '')
                    : group.values?.find((value) => value.id === values[group.id])?.label;
                if (label) parts.push(label);
            }
            return product.baseName + (parts.length ? ' · ' + parts.join(' · ') : '');
        },
        get configPrice() {
            if (!this.configPanel) return 0;
            const { product, values } = this.configPanel;
            const base = num(product.pricing.base ?? product.basePrice);
            let price = base;
            const ref = product.pricing.ref;
            if (ref && num(ref.bredd) * num(ref.hojd) > 0 && typeof values.bredd === 'number' && typeof values.hojd === 'number') {
                price = base * (0.35 + 0.65 * values.bredd * values.hojd / (ref.bredd * ref.hojd));
            }
            for (const group of product.optionGroups) {
                if (group.type === 'select') price += num(group.values?.find((value) => value.id === values[group.id])?.priceDelta);
            }
            return Math.round(price);
        },
        applyConfig() {
            if (!this.configPanel) return;
            const line = this.packages.flatMap((pkg) => pkg.items).find((item) => item.key === this.configPanel.key);
            if (line) {
                line.name = this.configName;
                line.price = this.configPrice;
                line.config = { ...this.configPanel.values };
            }
            this.closeConfig();
        },

        // Lines
        lineFrom(product, qty = 1) {
            const custom = Boolean(product.custom);
            return {
                key: 'l' + ++this.seq,
                line_key: product.line_key ?? null,
                id: product.id ?? null,
                name: custom ? (product.custom_name ?? product.name ?? '') : (product.name ?? ''),
                custom_description: custom ? (product.custom_description ?? '') : null,
                sku: custom ? null : (product.sku ?? null),
                image: (custom ? null : product.image) || this.profile.placeholderImage || null,
                variant: product.variant ?? '',
                price: num(custom ? (product.custom_price ?? product.price) : product.price),
                livePrice: num(product.live_price ?? product.livePrice ?? product.price),
                lockedPrice: num(product.locked_price ?? product.lockedPrice ?? product.price),
                liveMargin: product.live_margin ?? product.liveMargin ?? product.margin ?? null,
                lockedMargin: product.locked_margin ?? product.lockedMargin ?? product.margin ?? null,
                qty: Math.max(1, Math.round(num(qty)) || 1),
                disc: { mode: product.disc?.mode === 'pct' ? 'pct' : 'kr', val: num(product.disc?.val) },
                addon: Boolean(product.addon),
                seller_assigned_price: product.seller_assigned_price ?? null,
                note: product.note ?? null,
                noteOpen: noteText(product.note) !== null,
                optionGroups: product.optionGroups ?? [],
                baseName: product.baseName ?? product.name ?? '',
                basePrice: product.basePrice ?? product.price,
                pricing: product.pricing ?? {},
                nameAxes: product.nameAxes ?? null,
                config: { ...(product.config ?? {}) },
                custom,
                parent: product.parent ?? null,
                suggestions: (product.suggestions ?? []).map((item) => ({ ...item })),
                hidden_suggestions: [...(product.hidden_suggestions ?? [])],
                in_stock: product.in_stock ?? true,
                stock: product.stock ?? (product.in_stock === false ? 'out' : 'in'),
                deleted: Boolean(product.deleted),
                cost: product.cost ?? null,
                margin: product.margin ?? null,
                campaign: Boolean(product.campaign),
            };
        },
        copiedLine(line) {
            const price = line.custom ? line.price : line.livePrice;
            const margin = line.custom ? line.margin : line.liveMargin;
            return this.lineFrom({ ...line, line_key: null, price, lockedPrice: price, margin, lockedMargin: margin }, line.qty);
        },
        /** A product already in the package gets its quantity raised instead of a second row. */
        addLine(pkg, product, qty = 1) {
            const existing = pkg.items.find((line) => !line.custom && line.id === product.id);
            if (existing) {
                existing.qty += Math.max(1, Math.round(num(qty)) || 1);
                return existing;
            }
            const line = this.lineFrom(product, qty);
            pkg.items.push(line);
            return line;
        },
        /** Adds a catalogue item (a product, or a bundle as one line per bundle product) to a package. */
        addToPkg(pi, item) {
            const pkg = this.packages[pi];
            if (!pkg || !item) return;
            if (item.type === 'bundle') {
                for (const product of item.products ?? []) this.addLine(pkg, product, product.qty);
            } else {
                this.addLine(pkg, item, 1);
            }
            item._addedAt = ++this.actionSeq;
            this.normalize();
        },
        /** A free-text row ("+ egen rad"): the seller types the name and price; it never merges with anything. */
        addCustom(pi) {
            const pkg = this.packages[pi];
            if (!pkg) return;
            pkg.items.push(this.lineFrom({ custom: true, name: '', price: 0 }, 1));
            this.normalize();
        },
        setQty(pi, ii, delta) {
            const line = this.packages[pi]?.items[ii];
            if (!line) return;
            line.qty = Math.max(1, Math.round(num(line.qty)) + delta);
        },
        /** The note button is accented while the line carries a note, as the seller types. */
        hasNote(line) {
            return noteText(line.note) !== null;
        },
        /** Shows or hides the customer note area under the row; the note itself stays. */
        toggleNote(line, row) {
            line.noteOpen = !line.noteOpen;
            if (line.noteOpen) this.$nextTick(() => row?.querySelector('.sb-line__note-input')?.focus());
        },
        removeLine(pi, ii) {
            const pkg = this.packages[pi];
            if (!pkg || !pkg.items[ii]) return;
            const gone = pkg.items[ii];
            pkg.items = pkg.items.filter((line, index) => index !== ii && !this.isChildOf(line, gone));
            this.normalize();
        },

        // Math as written in 2.1 paket.js, except that a kr line discount is per unit, which is
        // what the platform stores and what the customer page and checkout charge.
        discountActive(pkg) {
            return !pkg.discount_valid_until || pkg.discount_valid_until >= this.serverToday;
        },
        lineDisc(line, pkg = null) {
            if (pkg && !this.discountActive(pkg)) return 0;
            const val = num(line.disc?.val);
            return line.disc?.mode === 'pct' ? num(line.price) * num(line.qty) * val / 100 : val * num(line.qty);
        },
        lineDiscInfo(line, pkg = null) {
            const base = num(line.price) * num(line.qty);
            const disc = this.lineDisc(line, pkg);
            const has = disc > 0 && base > 0;
            const unitNow = Math.max(0, num(line.price) - (num(line.qty) > 0 ? disc / num(line.qty) : 0));
            const opp = line.disc?.mode === 'pct'
                ? '−' + this.money(disc) + ' kr'
                : '−' + Math.round(base > 0 ? disc / base * 100 : 0) + ' %';
            return { base, disc, has, unitNow, opp };
        },
        // Discount and validity fold away behind one line in a package without a discount (2.1
        // paket.js v305). Unfolding is screen state only and is never saved.
        hasDiscount(pkg) {
            return num(pkg.disc?.val) > 0 || pkg.items.some((line) => num(line.disc?.val) > 0);
        },
        discVisible(pkg) {
            return this.hasDiscount(pkg) || Boolean(pkg.discOpen);
        },
        /**
         * Setting a discount unfolds the block and removing the last one folds it again, except
         * while the seller is still typing in the block's own field: then it folds when they leave it.
         */
        syncDisc(pkg, typingInBlock = false) {
            if (this.hasDiscount(pkg)) return;
            pkg.discOpen = typingInBlock;
        },
        pkgLineDisc(pkg) {
            return Math.round(pkg.items.filter((line) => !line.addon).reduce((sum, line) => sum + this.lineDisc(line, pkg), 0));
        },
        pkgOrig(pkg) {
            return pkg.items.filter((line) => !line.addon).reduce((sum, line) => sum + num(line.price) * num(line.qty), 0);
        },
        pkgTotal(pkg) {
            const sub = pkg.items.filter((line) => !line.addon).reduce((sum, line) => sum + (num(line.price) * num(line.qty) - this.lineDisc(line, pkg)), 0);
            if (!this.discountActive(pkg)) return sub;
            const val = num(pkg.disc?.val);
            const disc = pkg.disc?.mode === 'pct' ? sub * val / 100 : val;
            return Math.max(0, sub - disc);
        },
        get vatSwitchable() {
            return num(this.profile.vatPercentage) > 0;
        },
        get vatRatio() {
            return 1 + Math.max(0, num(this.profile.vatPercentage)) / 100;
        },
        get showsExVat() {
            return this.exVat && this.vatSwitchable;
        },
        setExVat(exVat) {
            this.exVat = exVat;
            storeExVat(exVat);
        },
        money(value) {
            return money(this.showsExVat ? num(value) / this.vatRatio : value);
        },
        storedMoney: money,
        krField(value, mode = 'kr') {
            if (value === '' || value === null || value === undefined) return '';
            return String(mode === 'kr' && this.showsExVat ? Math.round(num(value) / this.vatRatio) : value);
        },
        get inputRatio() {
            return this.showsExVat ? this.vatRatio : 1;
        },
        setDisc(disc, typed) {
            disc.val = storedAmount(typed, disc.mode === 'kr' ? this.inputRatio : 1, 0);
        },
        setCustomPrice(line, typed) {
            line.price = storedAmount(typed, this.inputRatio, 2);
        },

        // Margins as written in 2.1 paket.js: a line's margin is the server's percentage, "n/a" without
        // a cost; after a discount the pill reads "(m) nm%"; a package pro-rates its own discount over
        // the lines with a known margin. Thresholds are fixed at 15 and 30 (PRD #1761).
        margTone(margin) {
            if (margin === null || margin === undefined) return 'is-na';
            if (margin < 15) return 'is-low';
            if (margin < 30) return 'is-mid';
            return '';
        },
        margText(margin) {
            return margin === null || margin === undefined ? 'n/a' : Math.round(margin) + '%';
        },
        itemMargData(line, pkg = null) {
            const margin = line.custom || line.margin === null || line.margin === undefined ? null : num(line.margin);
            if (margin === null) return { m: null, nm: null, txt: 'n/a' };
            const base = num(line.price) * num(line.qty);
            const disc = this.lineDisc(line, pkg);
            const revenue = base - disc;
            const cost = base * (1 - margin / 100);
            const m = Math.round(margin);
            const nm = disc > 0 && revenue > 0 ? Math.round((revenue - cost) / revenue * 100) : m;
            return { m, nm, txt: disc > 0 && nm !== m ? '(' + m + ') ' + nm + '%' : nm + '%' };
        },
        knownLines(pkg) {
            return pkg.items.filter((line) => !line.addon && !line.custom && line.margin !== null && line.margin !== undefined);
        },
        pkgMargin(pkg) {
            const known = this.knownLines(pkg);
            if (known.length === 0) return null;
            const cost = known.reduce((sum, line) => sum + num(line.price) * num(line.qty) * (1 - num(line.margin) / 100), 0);
            const subKnown = known.reduce((sum, line) => sum + (num(line.price) * num(line.qty) - this.lineDisc(line, pkg)), 0);
            const subAll = pkg.items.filter((line) => !line.addon).reduce((sum, line) => sum + (num(line.price) * num(line.qty) - this.lineDisc(line, pkg)), 0);
            const val = this.discountActive(pkg) ? num(pkg.disc?.val) : 0;
            const pkgDisc = pkg.disc?.mode === 'pct' ? subAll * val / 100 : val;
            const revenue = subKnown - (subAll > 0 ? pkgDisc * (subKnown / subAll) : 0);
            return revenue > 0 ? Math.round((revenue - cost) / revenue * 100) : null;
        },
        pkgMarginOrig(pkg) {
            const known = this.knownLines(pkg);
            if (known.length === 0) return null;
            const cost = known.reduce((sum, line) => sum + num(line.price) * num(line.qty) * (1 - num(line.margin) / 100), 0);
            const revenue = known.reduce((sum, line) => sum + num(line.price) * num(line.qty), 0);
            return revenue > 0 ? Math.round((revenue - cost) / revenue * 100) : null;
        },
        pkgMargData(pkg) {
            const nm = this.pkgMargin(pkg);
            const om = this.pkgMarginOrig(pkg);
            if (nm === null) return { nm: null, txt: 'n/a', low: false };
            const hasDisc = this.pkgOrig(pkg) - this.pkgTotal(pkg) > 0;
            return { nm, txt: hasDisc && om !== null && om !== nm ? '(' + om + ') ' + nm + '%' : nm + '%', low: nm < 15 };
        },
        /** 1-based positions of the non-empty packages whose total margin is known and under 15 %. */
        lowMarginPackages() {
            return this.packages
                .filter((pkg) => pkg.items.length > 0)
                .map((pkg, index) => {
                    const margin = this.pkgMargin(pkg);
                    return margin !== null && margin < 15 ? index + 1 : null;
                })
                .filter((position) => position !== null);
        },
        get lowMarginMessage() {
            const low = this.lowMarginPackages();
            if (low.length === 0) return '';
            return (low.length === 1 ? 'Alternativ ' + low[0] : 'Alternativen ' + low.join(', '))
                + ' har en total marginal under 15 %. Vill du spara ändå?';
        },
        closeWarn() {
            this.warnOpen = false;
        },

        // Catalogue
        productIds(item) {
            return item.type === 'bundle' ? (item.products ?? []).map((product) => product.id) : [item.id];
        },
        inPackage(pkg, item) {
            const ids = this.productIds(item);
            return ids.length > 0 && ids.every((id) => pkg.items.some((line) => !line.custom && line.id === id));
        },
        /** The alternatives a product card is badged with; a ready-made bundle card never carries these badges. */
        packagesWith(item) {
            if (item.type === 'bundle') return [];
            return this.packages
                .map((pkg, pi) => (this.inPackage(pkg, item) ? this.packageLabel(pi) : null))
                .filter((name) => name !== null);
        },
        get selectedItem() {
            return this.catalog.items.find((item) => item.key === this.selected) ?? null;
        },
        isHighlighted(line) {
            const item = this.selectedItem;
            return item !== null && !line.custom && this.productIds(item).includes(line.id);
        },
        select(item) {
            this.selected = item.key;
            item._clickedAt = ++this.actionSeq;
            this.$nextTick(() => this.$root.querySelector('[data-hl]')?.scrollIntoView?.({ block: 'nearest' }));
        },
        /** A click anywhere outside the detail pane and the catalogue collapses the pane. */
        collapseDetail(event) {
            if (this.selected === null) return;
            if (event.target?.closest?.('.sb-builder__detail, .sb-builder__catalog, .sb-multi, .sb-builder__web, .sb-builder__vat')) return;
            this.selected = null;
        },

        // The product's web page in an overlay
        openWeb(item) {
            if (!item?.url) return;
            this.web = { open: true, url: item.url };
        },
        closeWeb() {
            this.web = { open: false, url: '' };
        },
        get sortedCatalog() {
            const items = [...this.catalog.items];
            if (this.catalog.sort === 'clicked') return items.sort((a, b) => (b._clickedAt ?? 0) - (a._clickedAt ?? 0));
            if (this.catalog.sort === 'added') return items.sort((a, b) => (b._addedAt ?? 0) - (a._addedAt ?? 0));
            return items;
        },
        get catalogFooter() {
            if (!this.catalog.searched) return '';
            const shown = this.catalog.items.length;
            return this.catalog.total > shown
                ? 'Visar ' + shown + ' av ' + this.catalog.total + ' – sök för att förfina'
                : this.catalog.total + ' produkter';
        },
        queryChanged() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.search(), SearchDelayMs);
        },
        /** The chips as the server takes them; stock chips combine with OR, the others are single. */
        chipFilters() {
            return {
                stock: [...this.catalog.stock],
                margin: this.catalog.margin,
                campaign: this.catalog.campaign,
                showPackages: this.catalog.showPackages,
                showProducts: this.catalog.showProducts,
            };
        },
        /** As in 2.1: each stock chip on, a margin or campaign chip off "all", and each kind chip switched off count as one. */
        get activeFilterCount() {
            return this.catalog.stock.length
                + (this.catalog.margin !== 'all' ? 1 : 0)
                + (this.catalog.campaign !== 'all' ? 1 : 0)
                + (this.catalog.showPackages ? 0 : 1)
                + (this.catalog.showProducts ? 0 : 1);
        },
        get filterToggleTitle() {
            const count = this.activeFilterCount;
            return count === 0 ? 'Visa filter' : count + (count === 1 ? ' filter aktivt' : ' filter aktiva');
        },
        toggleFilters() {
            this.catalog.filtersOpen = !this.catalog.filtersOpen;
        },
        /** "Visa paket" (bundles) or "Visa artiklar" (products); one of them always stays on. */
        toggleKind(kind) {
            const [key, other] = { packages: ['showPackages', 'showProducts'], products: ['showProducts', 'showPackages'] }[kind];
            if (this.catalog[key] && !this.catalog[other]) return;
            this.catalog[key] = !this.catalog[key];
            this.search();
        },
        /** A bundle card's product names, in place of a product's article number. */
        bundleContents(item, separator) {
            return (item.products ?? []).map((product) => product.name).join(separator);
        },
        bundleRowMeta(product) {
            return [product.brand, product.sku ? 'Art.nr: ' + product.sku : null].filter(Boolean).join(' · ');
        },
        toggleStock(state) {
            this.catalog.stock = this.catalog.stock.includes(state)
                ? this.catalog.stock.filter((candidate) => candidate !== state)
                : [...this.catalog.stock, state];
            this.search();
        },
        setMargin(chip) {
            this.catalog.margin = chip;
            this.search();
        },
        setCampaign(chip) {
            this.catalog.campaign = chip;
            this.search();
        },
        /** "Kampanj −N %": how far the feed's original price sits above the current one. Cosmetic only. */
        campaignPct(item) {
            const original = num(item.original_price);
            return original > 0 ? Math.round((original - num(item.price)) / original * 100) : 0;
        },
        availabilityLabel(item) {
            if (!item) return '';
            if (item.deleted) return 'Utgått';
            return item.stock === 'out' ? 'Slut i lager' : 'I lager';
        },
        sortChanged() {
            if (ServerSorts.includes(this.catalog.sort)) this.search();
        },
        async search() {
            clearTimeout(this.timer);
            const revision = ++this.revision;
            const sort = ServerSorts.includes(this.catalog.sort) ? this.catalog.sort : 'rel';
            this.catalog.loading = true;
            try {
                const result = await this.$wire.search(this.catalog.query, sort, this.chipFilters());
                if (revision !== this.revision) return;
                const previous = new Map(this.catalog.items.map((item) => [item.key, item]));
                this.catalog.items = (result.items ?? []).slice(0, CatalogCap).map((item) => ({
                    ...item,
                    key: item.type + '-' + item.id,
                    ...(item.type === 'bundle' ? { products: (item.products ?? []).map((product) => ({ ...product, key: 'product-' + product.id })) } : {}),
                    _clickedAt: previous.get(item.type + '-' + item.id)?._clickedAt ?? 0,
                    _addedAt: previous.get(item.type + '-' + item.id)?._addedAt ?? 0,
                }));
                this.catalog.total = num(result.total);
                this.catalog.searched = true;
            } catch (error) {
                if (revision !== this.revision) return;
                this.error = 'Katalogen kunde inte hämtas. Försök igen.';
                this.report(error);
            } finally {
                if (revision === this.revision) this.catalog.loading = false;
            }
        },

        // The house-style date picker (2.1 datepicker.js) for the two validity dates of a package
        dateLabel(iso) {
            const date = parseIso(iso);
            return date ? date.getDate() + ' ' + MonthsShort[date.getMonth()] + ' ' + date.getFullYear() : '';
        },
        refreshLockPrices(pkg) {
            const locked = pkg.price_locked_until && pkg.price_locked_until >= this.serverToday;
            for (const line of pkg.items) {
                if (line.custom) continue;
                line.price = locked ? line.lockedPrice : line.livePrice;
                line.margin = locked ? line.lockedMargin : line.liveMargin;
            }
        },
        openPicker(pi, field, anchor) {
            if (this.picker.open && this.picker.pi === pi && this.picker.field === field) return this.closePicker();
            const current = parseIso(this.packages[pi]?.[field]) ?? new Date();
            this.pickerAnchor = anchor;
            this.picker = { open: true, pi, field, year: current.getFullYear(), month: current.getMonth(), style: pickerStyle(anchor, PickerHeight) };
            this.$nextTick(() => this.placePicker());
        },
        /** Keeps the open picker beside its date field while a column scrolls, as 2.1's datepicker.js does. */
        placePicker() {
            if (this.picker.open) this.picker.style = pickerStyle(this.pickerAnchor, this.$refs.datePopup?.getBoundingClientRect().height || PickerHeight);
        },
        closePicker() {
            if (this.picker.open) this.picker = { ...this.picker, open: false, style: 'display:none' };
        },
        get pickerTitle() {
            return Months[this.picker.month] + ' ' + this.picker.year;
        },
        /** Monday-first month grid: leading blanks, then one cell per day with today and the chosen date marked. */
        get pickerCells() {
            const { year, month, pi, field } = this.picker;
            const selected = this.packages[pi]?.[field] ?? null;
            const today = isoOf(new Date());
            const start = (new Date(year, month, 1).getDay() + 6) % 7;
            const count = new Date(year, month + 1, 0).getDate();
            const cells = [];
            for (let blank = 0; blank < start; blank++) cells.push({ key: 'b' + blank, day: null, iso: null, selected: false, today: false });
            for (let day = 1; day <= count; day++) {
                const iso = year + '-' + pad(month + 1) + '-' + pad(day);
                cells.push({ key: iso, day, iso, selected: iso === selected, today: iso === today });
            }
            return cells;
        },
        prevMonth() {
            const date = new Date(this.picker.year, this.picker.month - 1, 1);
            this.picker = { ...this.picker, year: date.getFullYear(), month: date.getMonth() };
        },
        nextMonth() {
            const date = new Date(this.picker.year, this.picker.month + 1, 1);
            this.picker = { ...this.picker, year: date.getFullYear(), month: date.getMonth() };
        },
        pickDate(iso) {
            const pkg = this.packages[this.picker.pi];
            if (pkg && this.picker.field) {
                pkg[this.picker.field] = iso || null;
                if (this.picker.field === 'price_locked_until') this.refreshLockPrices(pkg);
            }
            this.closePicker();
        },
        pickToday() {
            this.pickDate(isoOf(new Date()));
        },
        clearDate() {
            this.pickDate(null);
        },

        // Multi-add popover
        openMulti(item, anchor) {
            this.normalize();
            const rect = anchor?.getBoundingClientRect?.() ?? { right: 0, top: 0 };
            const left = Math.max(8, Math.min(rect.right + 8, window.innerWidth - MultiWidth - 8));
            const popupHeight = Math.min(400, this.packages.length * 42 + 112);
            const top = Math.max(8, Math.min(rect.top, window.innerHeight - popupHeight - 8));
            this.multi = { open: true, item, checked: [], style: 'display:block;left:' + left + 'px;top:' + top + 'px' };
            this.$nextTick(() => {
                if (!this.multi.open) return;
                const height = this.$refs.multiPopup?.getBoundingClientRect().height || popupHeight;
                this.multi.style = 'display:block;left:' + left + 'px;top:' + Math.max(8, Math.min(rect.top, window.innerHeight - height - 8)) + 'px';
            });
        },
        closeMulti() {
            this.multi = { open: false, item: null, checked: [], style: 'display:none' };
        },
        multiPresent(pi) {
            return this.multi.item !== null && this.inPackage(this.packages[pi], this.multi.item);
        },
        toggleMulti(pi) {
            if (this.multiPresent(pi)) return;
            this.multi.checked = this.multi.checked.includes(pi)
                ? this.multi.checked.filter((index) => index !== pi)
                : [...this.multi.checked, pi];
        },
        applyMulti() {
            const item = this.multi.item;
            const targets = [...this.multi.checked].sort((a, b) => a - b);
            if (!item || targets.length === 0) return;
            for (const pi of targets) this.addToPkg(pi, item);
            this.closeMulti();
        },

        suggestionsFor(pkg, line) {
            if (this.isChild(line)) return [];
            return line.suggestions.filter((item) => !line.hidden_suggestions.includes(item.id)
                && !pkg.items.some((existing) => !existing.custom && existing.id === item.id));
        },
        addSuggestion(pkg, line, suggestion) {
            if (!this.suggestionsFor(pkg, line).some((item) => item.id === suggestion.id)) return;
            const child = this.lineFrom({ ...suggestion, parent: line.id });
            const at = pkg.items.indexOf(line) + 1 + this.childrenOf(pkg, line).length;
            pkg.items.splice(at, 0, child);
        },
        /** A product's open suggestions get their own rows under its last accessory, or under the product itself (2.1 itemsHtml). */
        suggestionsAfter(pkg, ii) {
            const line = pkg.items[ii];
            const parent = line && this.isChild(line) ? pkg.items.find((candidate) => !this.isChild(candidate) && this.isChildOf(line, candidate)) : line;
            if (!parent) return [];
            const last = Math.max(...[parent, ...this.childrenOf(pkg, parent)].map((member) => pkg.items.indexOf(member)));
            if (last !== ii) return [];
            return this.suggestionsFor(pkg, parent).map((suggestion) => ({ key: parent.key + '-' + suggestion.id, parent, suggestion }));
        },
        hideSuggestion(line, suggestion) {
            if (!line.hidden_suggestions.includes(suggestion.id)) line.hidden_suggestions.push(suggestion.id);
        },

        // Accessory children: a line whose parent points at another line's key (added here) or product id (loaded)
        isChild(line) {
            return line.parent !== null && line.parent !== undefined;
        },
        isChildOf(line, parentLine) {
            return this.isChild(line) && (line.parent === parentLine.key || (!parentLine.custom && line.parent === parentLine.id));
        },
        childrenOf(pkg, parentLine) {
            return pkg.items.filter((line) => this.isChildOf(line, parentLine));
        },

        // Drag and drop from the catalogue, and rows between and within packages
        dragStart(item, event) {
            this.dragging = item;
            event.dataTransfer?.setData('text/plain', item.key);
            if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
        },
        dragOver(event) {
            this.dropMark = null;
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        },
        /** Free-text rows stay where they are: they are not draggable and never merge into another package. */
        dragRow(pi, ii, event) {
            if (this.packages[pi]?.items[ii]?.custom) return event.preventDefault?.();
            this.rowDrag = { pi, ii };
            event.dataTransfer?.setData('text/plain', 'move:' + pi + ':' + ii);
            if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
        },
        dragRowEnd() {
            this.rowDrag = null;
            this.dropMark = null;
        },
        /** Within one package a row lands above or below the row under the pointer (never a child row); elsewhere it copies. */
        rowDragOver(pi, ii, event) {
            const target = this.packages[pi]?.items[ii];
            const src = this.rowDrag;
            if (src && target && src.pi === pi && src.ii !== ii && !this.isChild(target)) {
                const rect = event.currentTarget?.getBoundingClientRect?.() ?? { top: 0, height: 0 };
                this.dropMark = { pi, ii, after: event.clientY > rect.top + rect.height / 2 };
                if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
                return;
            }
            this.dropMark = null;
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        },
        markAt(pi, ii) {
            const mark = this.dropMark;
            return mark && mark.pi === pi && mark.ii === ii ? (mark.after ? 'after' : 'before') : null;
        },
        dropOn(pi, event) {
            const data = event.dataTransfer?.getData('text/plain') || '';
            const mark = this.dropMark;
            this.dropMark = null;
            const moved = this.rowDrag ?? (data.startsWith('move:') ? { pi: Number(data.split(':')[1]), ii: Number(data.split(':')[2]) } : null);
            this.rowDrag = null;
            if (moved) {
                if (moved.pi !== pi) this.copyLineTo(moved.pi, moved.ii, pi);
                else if (mark && mark.pi === pi) this.reorder(pi, moved.ii, mark.ii, mark.after);
                return;
            }
            const key = data || this.dragging?.key;
            const item = this.catalog.items.find((candidate) => candidate.key === key) ?? this.dragging;
            this.dragging = null;
            if (item) this.addToPkg(pi, item);
        },
        /** Moves a row and its accessories to just before or after the target row (after its accessories). */
        reorder(pi, from, to, after) {
            const pkg = this.packages[pi];
            const line = pkg?.items[from];
            const target = pkg?.items[to];
            if (!line || !target || this.isChild(line)) return;
            const group = [line, ...this.childrenOf(pkg, line)];
            if (group.includes(target)) return;
            const rest = pkg.items.filter((candidate) => !group.includes(candidate));
            let at = rest.indexOf(target);
            if (after) {
                at++;
                while (at < rest.length && this.isChildOf(rest[at], target)) at++;
            }
            rest.splice(at, 0, ...group);
            pkg.items = rest;
        },
        /** Copies one row into another package (the source keeps it); a product already there gets the quantity added. */
        copyLineTo(from, index, to) {
            const src = this.packages[from];
            const dst = this.packages[to];
            const line = src?.items[index];
            if (!line || !dst || from === to) return;
            const existing = line.custom ? null : dst.items.find((candidate) => !candidate.custom && candidate.id === line.id);
            if (existing) existing.qty += Math.max(1, Math.round(num(line.qty)) || 1);
            else dst.items.push(this.copiedLine({ ...line, parent: null }));
            this.normalize();
        },

        // The divider between the catalogue and the packages
        startResize(event) {
            this.stopResize();
            this.resizing = true;
            const startX = event.clientX;
            const startWidth = this.catalogWidth;
            this.resizeMove = (move) => {
                this.catalogWidth = Math.min(CatalogMaxWidth, Math.max(CatalogMinWidth, startWidth + move.clientX - startX));
            };
            this.resizeStop = () => {
                storeWidth(this.catalogWidth);
                this.stopResize();
            };
            document.addEventListener('mousemove', this.resizeMove);
            document.addEventListener('mouseup', this.resizeStop);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
        },
        stopResize() {
            if (this.resizeMove) document.removeEventListener('mousemove', this.resizeMove);
            if (this.resizeStop) document.removeEventListener('mouseup', this.resizeStop);
            this.resizeMove = null;
            this.resizeStop = null;
            this.resizing = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        },

        // Saving
        /** The exact set sent to the server: non-empty packages in order, in the PRD package shape. */
        payload() {
            return this.packages
                .filter((pkg) => pkg.items.length > 0)
                .map((pkg) => ({
                    id: pkg.id ?? null,
                    name: (pkg.name ?? '').trim(),
                    recommended: Boolean(pkg.recommended),
                    disc: { mode: pkg.disc.mode, val: num(pkg.disc.val) },
                    discount_valid_until: pkg.discount_valid_until ?? null,
                    signable_until: pkg.signable_until ?? null,
                    price_locked_until: pkg.price_locked_until ?? null,
                    pay: pkg.pay ?? null,
                    items: pkg.items.map((line) => ({
                        id: line.custom ? null : line.id,
                        line_key: line.line_key,
                        qty: Math.max(1, Math.round(num(line.qty)) || 1),
                        disc: { mode: line.disc.mode, val: num(line.disc.val) },
                        addon: Boolean(line.addon),
                        seller_assigned_price: line.custom ? null : (line.seller_assigned_price ?? null),
                        note: noteText(line.note),
                        custom: Boolean(line.custom),
                        custom_name: line.custom ? String(line.name ?? '').trim() : null,
                        ...(line.custom ? { custom_description: noteText(line.custom_description) } : {}),
                        custom_price: line.custom ? num(line.price) : null,
                        parent: pkg.items.find((candidate) => candidate.key === line.parent)?.id ?? line.parent ?? null,
                        ...(line.hidden_suggestions.length ? { hidden_suggestions: [...line.hidden_suggestions] } : {}),
                    })),
                }));
        },
        /** Uppdatera warns once about packages under 15 % margin; "Spara ändå" calls back with force. */
        async save(force = false) {
            if (this.saving || this.loading || !this.loadSucceeded) return;
            const invalid = this.payload().some((pkg) => [pkg.disc, ...pkg.items.map((item) => item.disc)]
                .some((disc) => !Number.isInteger(disc.val) || disc.val < 0 || (disc.mode === 'pct' && disc.val > 100)));
            if (invalid) {
                this.error = 'Ange hela rabatter i kronor eller procent mellan 0 och 100.';
                return;
            }
            this.closeMulti();
            if (!force && this.lowMarginPackages().length > 0) {
                this.warnOpen = true;
                this.$nextTick(() => this.$refs.keepEditingWarn?.focus());
                return;
            }
            this.warnOpen = false;
            this.saving = true;
            this.error = '';
            this.adv = false;
            this.closePicker();
            try {
                if (this.profile.createMode) {
                    this.draft = { packages: JSON.parse(JSON.stringify(this.packages)), payload: { packages: this.payload(), settings: this.settingsPayload() } };
                    this.applyFormSwitches();
                    this.snapshot();
                    this.closeBuilder();
                    return;
                }
                const result = await this.$wire.save(this.payload(), this.settingsPayload(), this.notifyPayload(), this.fingerprint);
                this.fingerprint = result.fingerprint ?? null;
                this.quote = { ...this.quote, ...(result.quote ?? {}) };
                this.restoreSettings();
                this.fresh(result.packages ?? []);
                this.snapshot();
                this.closeBuilder();
                this.reloadPage();
            } catch (error) {
                this.error = error?.status === 422
                    ? (Object.values(error.errors ?? {}).flat()[0] ?? 'Ändringarna kunde inte sparas. Kontrollera paketen och försök igen.')
                    : 'Ändringarna kunde inte sparas. Försök igen.';
                if (error?.status !== 422) this.report(error);
            } finally {
                this.saving = false;
            }
        },
        /** The quote page (offer card, timeline, header value) is server-rendered; a reload shows the saved set. */
        reloadPage() {
            window.location.reload();
        },
        report(error) {
            window.SalesByteCaptureException?.(error, { tags: { feature: 'package-builder' } });
        },
    }));
});
